// Settings: Workspace (everyone can see, admin can change), Knowledge (what the AI knows),
// AI (switch + instructions), My notifications (per person), Users (admin), Lines (admin).

import { useEffect, useState, type FormEvent } from 'react';
import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatPhone, normalizeE164, type ProviderId, countSegments, describeUcs2Triggers } from '@bt/core';
import { api, type Line, type MeResponse, type User } from '../../api';
import { Dialog } from '../../components/Dialog';
import { Loading, ErrorBox, Empty, InlineError } from '../../components/States';
import { useToast } from '../../components/Toast';
import { useUsers } from '../../hooks/useMe';
import { fmtFull, hhmm, hhmmWords, linePaused, makeTempPassword } from '../../lib/format';
import { COMMON_TIMEZONES } from '../../lib/timezone';
import { KnowledgeSettings } from './KnowledgeSettings';
import { AiSettings } from './AiSettings';
import { NotificationsSettings } from './NotificationsSettings';

export function SettingsPage({ me }: { me: MeResponse }) {
  const isAdmin = me.user.role === 'admin';
  return (
    <div className="page narrow">
      <h1>Settings</h1>
      <div className="settings-nav">
        <NavLink to="/settings" end className={({ isActive }) => (isActive ? 'active' : '')}>
          Workspace
        </NavLink>
        <NavLink to="/settings/knowledge" className={({ isActive }) => (isActive ? 'active' : '')}>
          Knowledge
        </NavLink>
        <NavLink to="/settings/ai" className={({ isActive }) => (isActive ? 'active' : '')}>
          AI
        </NavLink>
        <NavLink to="/settings/notifications" className={({ isActive }) => (isActive ? 'active' : '')}>
          My notifications
        </NavLink>
        {isAdmin && (
          <NavLink to="/settings/users" className={({ isActive }) => (isActive ? 'active' : '')}>
            Users
          </NavLink>
        )}
        {isAdmin && (
          <NavLink to="/settings/lines" className={({ isActive }) => (isActive ? 'active' : '')}>
            Lines
          </NavLink>
        )}
      </div>
      <Routes>
        <Route index element={<WorkspaceSettings me={me} />} />
        <Route path="knowledge" element={<KnowledgeSettings />} />
        <Route path="ai" element={<AiSettings me={me} />} />
        <Route path="notifications" element={<NotificationsSettings />} />
        <Route path="users" element={isAdmin ? <UsersSettings me={me} /> : <Navigate to="/settings" replace />} />
        <Route path="lines" element={isAdmin ? <LinesSettings me={me} /> : <Navigate to="/settings" replace />} />
        <Route path="*" element={<Navigate to="/settings" replace />} />
      </Routes>
    </div>
  );
}

// ---------------- Workspace ----------------

function WorkspaceSettings({ me }: { me: MeResponse }) {
  const qc = useQueryClient();
  const toast = useToast();
  const users = useUsers();
  const w = me.workspace;
  const isAdmin = me.user.role === 'admin';
  const [form, setForm] = useState({
    name: w.name,
    company_name: w.company_name ?? '',
    timezone: w.timezone,
    send_window_start: hhmm(w.send_window_start) || '08:00',
    send_window_end: hhmm(w.send_window_end) || '21:00',
    quiet_reply_exception: w.quiet_reply_exception,
    allow_quiet_hours_override: w.allow_quiet_hours_override,
    default_assignee_user_id: w.default_assignee_user_id ?? '',
    alert_webhook_url: w.alert_webhook_url ?? '',
  });

  const save = useMutation({
    mutationFn: () =>
      api.workspace.update({
        name: form.name.trim(),
        company_name: form.company_name.trim(),
        timezone: form.timezone,
        send_window_start: form.send_window_start,
        send_window_end: form.send_window_end,
        quiet_reply_exception: form.quiet_reply_exception,
        allow_quiet_hours_override: form.allow_quiet_hours_override,
        default_assignee_user_id: form.default_assignee_user_id || null,
        alert_webhook_url: form.alert_webhook_url.trim() || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me'] });
      toast({ text: 'Saved.', tone: 'ok' });
    },
  });
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));
  // WHY (review round 1, trap 5): the name goes into EVERY automatic text; a smart-punctuation apostrophe would make them all UCS-2
  const companyEncoding = form.company_name && countSegments(form.company_name).encoding === 'ucs2' ? describeUcs2Triggers(form.company_name) + '.' : null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  return (
    <form className="card" onSubmit={submit}>
      {!w.company_name && <div className="notice warn" style={{ marginBottom: 12 }}>Set the company name. Nothing can be sent without it (it signs the opt-out replies).</div>}
      <label className="field">
        <span>Company name (required)</span>
        <input className="input" value={form.company_name} onChange={(e) => set('company_name', e.target.value)} disabled={!isAdmin} required />
        <div className="field-help">Used in automatic replies: “{form.company_name || 'Company'}: You're opted out and won't get more texts from us.”</div>
        {companyEncoding && (
          <div className="tiny warn-text">
            {companyEncoding} It is saved as plain text (a straight ' instead), so every automatic text stays a 160-character segment.
          </div>
        )}
      </label>
      <label className="field">
        <span>Workspace name</span>
        <input className="input" value={form.name} onChange={(e) => set('name', e.target.value)} disabled={!isAdmin} />
      </label>
      <label className="field">
        <span>Office time zone</span>
        <select className="input" value={form.timezone} onChange={(e) => set('timezone', e.target.value)} disabled={!isAdmin}>
          {!COMMON_TIMEZONES.some((z) => z.id === form.timezone) && <option value={form.timezone}>{form.timezone}</option>}
          {COMMON_TIMEZONES.map((z) => (
            <option key={z.id} value={z.id}>
              {z.label}
            </option>
          ))}
        </select>
      </label>
      <div className="row" style={{ alignItems: 'flex-start' }}>
        <label className="field grow">
          <span>Texting hours start</span>
          <input className="input" type="time" value={form.send_window_start} onChange={(e) => set('send_window_start', e.target.value)} disabled={!isAdmin} />
        </label>
        <label className="field grow">
          <span>Texting hours end</span>
          <input className="input" type="time" value={form.send_window_end} onChange={(e) => set('send_window_end', e.target.value)} disabled={!isAdmin} />
        </label>
      </div>
      <div className="field-help" style={{ marginTop: -6, marginBottom: 12 }}>
        Texts go out between {hhmmWords(form.send_window_start)} and {hhmmWords(form.send_window_end)} in the customer's own zone. Set the end at least 10 minutes before 9 PM.
      </div>
      <label className="check">
        <input type="checkbox" checked={form.quiet_reply_exception} onChange={(e) => set('quiet_reply_exception', e.target.checked)} disabled={!isAdmin} />
        <span>
          <strong>Reply exception.</strong> A typed reply to someone who texted us in the last 24 hours may go out outside texting hours.
        </span>
      </label>
      <label className="check">
        <input type="checkbox" checked={form.allow_quiet_hours_override} onChange={(e) => set('allow_quiet_hours_override', e.target.checked)} disabled={!isAdmin} />
        <span>
          <strong>Allow “Send now”.</strong> Staff may force a reply out during quiet hours by giving a reason. It is logged.
        </span>
      </label>
      <label className="field">
        <span>New texts from unknown numbers go to</span>
        <select className="input" value={form.default_assignee_user_id} onChange={(e) => set('default_assignee_user_id', e.target.value)} disabled={!isAdmin}>
          <option value="">Nobody (Unassigned box)</option>
          {users
            .filter((u) => !u.disabled_at)
            .map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
        </select>
      </label>
      <label className="field">
        <span>Send alarms to this web address (optional)</span>
        <input className="input" value={form.alert_webhook_url} onChange={(e) => set('alert_webhook_url', e.target.value)} disabled={!isAdmin} placeholder="https://…  (a Slack or Telegram bridge that takes JSON)" />
        <div className="field-help">Every new alarm is POSTed here as plain JSON.</div>
      </label>
      <InlineError error={save.error} />
      {isAdmin && (
        <button className="btn primary" type="submit" disabled={save.isPending || !form.company_name.trim()}>
          Save
        </button>
      )}

    </form>
  );
}

// ---------------- Users ----------------

function UsersSettings({ me }: { me: MeResponse }) {
  const q = useQuery({ queryKey: ['users'], queryFn: api.users.list });
  const qc = useQueryClient();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [shown, setShown] = useState<{ email: string; password: string } | null>(null);
  const [resetFor, setResetFor] = useState<User | null>(null);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['users'] });
    qc.invalidateQueries({ queryKey: ['me'] });
  };
  const update = useMutation({
    mutationFn: (p: { id: string; body: Parameters<typeof api.users.update>[1] }) => api.users.update(p.id, p.body),
    onSuccess: refresh,
    onError: (e) => toast({ text: (e as Error).message, tone: 'error' }),
  });

  const items = q.data?.items ?? [];
  return (
    <div className="card">
      <div className="row between" style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Users</h2>
        <button className="btn primary small" onClick={() => setAdding(true)}>
          Add user
        </button>
      </div>
      {q.isPending && <Loading />}
      {q.isError && <ErrorBox error={q.error} retry={() => q.refetch()} />}
      {q.isSuccess && items.length === 0 && <Empty title="No users" />}
      {items.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Last seen</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} style={{ opacity: u.disabled_at ? 0.6 : 1 }}>
                  <td>
                    {u.name}
                    {u.id === me.user.id && <span className="muted small"> (you)</span>}
                    {u.disabled_at && <span className="chip" style={{ marginLeft: 6 }}>disabled</span>}
                  </td>
                  <td className="small">{u.email}</td>
                  <td>
                    <select className="input" value={u.role} onChange={(e) => update.mutate({ id: u.id, body: { role: e.target.value as 'admin' | 'agent' } })} disabled={u.id === me.user.id || update.isPending} style={{ width: 'auto', padding: '4px 8px' }}>
                      <option value="agent">Agent</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td className="muted small nowrap">{u.last_seen_at ? fmtFull(u.last_seen_at, me.workspace.timezone) : 'never'}</td>
                  <td className="right nowrap">
                    <button className="btn small" onClick={() => setResetFor(u)}>
                      Reset password
                    </button>{' '}
                    {u.id !== me.user.id &&
                      (u.disabled_at ? (
                        <button className="btn small" onClick={() => update.mutate({ id: u.id, body: { disabled: false } })} disabled={update.isPending}>
                          Enable
                        </button>
                      ) : (
                        <button className="btn small danger" onClick={() => confirm(`Disable ${u.name}? Their open conversations go back to Unassigned.`) && update.mutate({ id: u.id, body: { disabled: true } })} disabled={update.isPending}>
                          Disable
                        </button>
                      ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {adding && (
        <AddUserDialog
          onClose={() => setAdding(false)}
          onCreated={(email, password) => {
            refresh();
            setShown({ email, password });
          }}
        />
      )}
      {resetFor && (
        <ResetPasswordDialog
          user={resetFor}
          onClose={() => setResetFor(null)}
          onDone={(password) => {
            setShown({ email: resetFor.email, password });
            setResetFor(null);
          }}
        />
      )}
      {shown && (
        <Dialog title="Temporary password" onClose={() => setShown(null)}>
          <p>
            Give this to <strong>{shown.email}</strong>. It is shown once.
          </p>
          <div className="notice" style={{ fontSize: 18, textAlign: 'center' }}>
            <code style={{ fontSize: 18 }}>{shown.password}</code>
          </div>
          <div className="actions">
            <button className="btn" onClick={() => navigator.clipboard?.writeText(shown.password).catch(() => {})}>
              Copy
            </button>
            <button className="btn primary" onClick={() => setShown(null)}>
              Done
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
}

function AddUserDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (email: string, password: string) => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'agent'>('agent');
  const [password] = useState(() => makeTempPassword());
  const create = useMutation({
    mutationFn: () => api.users.create({ name: name.trim(), email: email.trim(), role, password }),
    onSuccess: () => {
      onCreated(email.trim(), password);
      onClose();
    },
  });
  return (
    <Dialog title="Add user" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <label className="field">
          <span>Name</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus required />
        </label>
        <label className="field">
          <span>Email</span>
          <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>
        <label className="field">
          <span>Role</span>
          <select className="input" value={role} onChange={(e) => setRole(e.target.value as 'admin' | 'agent')}>
            <option value="agent">Agent (texts, contacts, reminders)</option>
            <option value="admin">Admin (also users, lines, settings)</option>
          </select>
        </label>
        <div className="field-help">A temporary password is made for them and shown once after you save.</div>
        <InlineError error={create.error} />
        <div className="actions">
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="submit" disabled={!name.trim() || !email.trim() || create.isPending}>
            Add
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function ResetPasswordDialog({ user, onClose, onDone }: { user: User; onClose: () => void; onDone: (password: string) => void }) {
  const [password] = useState(() => makeTempPassword());
  const m = useMutation({
    mutationFn: () => api.users.update(user.id, { password }),
    onSuccess: () => onDone(password),
  });
  return (
    <Dialog title={`Reset password for ${user.name}`} onClose={onClose}>
      <p className="muted">A new temporary password replaces the old one right away. You will see it once.</p>
      <InlineError error={m.error} />
      <div className="actions">
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary" disabled={m.isPending} onClick={() => m.mutate()}>
          Reset
        </button>
      </div>
    </Dialog>
  );
}

// ---------------- Lines ----------------

const RISK_TEXT =
  'This line runs on a consumer phone plan. The carrier can shut it off without notice and there is no appeal. We recommend a registered number for any line the business depends on.';

function LinesSettings({ me }: { me: MeResponse }) {
  const q = useQuery({ queryKey: ['lines'], queryFn: api.lines.list });
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<Line | 'new' | null>(null);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['lines'] });
    qc.invalidateQueries({ queryKey: ['health'] });
    qc.invalidateQueries({ queryKey: ['me'] });
  };
  const update = useMutation({
    mutationFn: (p: { id: string; body: Parameters<typeof api.lines.update>[1] }) => api.lines.update(p.id, p.body),
    onSuccess: refresh,
    onError: (e) => toast({ text: (e as Error).message, tone: 'error' }),
  });
  const resume = useMutation({
    mutationFn: (id: string) => api.lines.resume(id),
    onSuccess: refresh,
    onError: (e) => toast({ text: (e as Error).message, tone: 'error' }),
  });
  const items = q.data?.items ?? [];
  const tz = me.workspace.timezone;

  return (
    <div className="card">
      <div className="row between" style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Texting lines</h2>
        <button className="btn primary small" onClick={() => setEditing('new')}>
          Add line
        </button>
      </div>
      {!me.workspace.company_name && <div className="notice warn" style={{ marginBottom: 12 }}>Set the company name under Workspace before a line can send.</div>}
      {q.isPending && <Loading />}
      {q.isError && <ErrorBox error={q.error} retry={() => q.refetch()} />}
      {q.isSuccess && items.length === 0 && <Empty title="No lines yet" hint="Add the phone (httpSMS) or a registered number (Telnyx or TextGrid)." />}
      <ul className="list-plain">
        {items.map((l) => {
          const { paused } = linePaused(l);
          return (
            <li key={l.id}>
              <div className="row between wrap">
                <div>
                  <strong>{l.label ? `${l.label} · ` : ''}{formatPhone(l.phone_number)}</strong>
                  <div className="row wrap" style={{ marginTop: 4 }}>
                    <span className={`chip ${l.provider === 'httpsms' ? 'warn' : 'ok'}`}>{l.provider === 'httpsms' ? 'Phone line: can be cut off without notice' : 'Registered line'}</span>
                    <span className="chip">{l.provider}</span>
                    {!l.active && <span className="chip">off</span>}
                    {paused && <span className="chip danger">paused</span>}
                    {l.last_self_test_passed_at ? <span className="chip ok">self-test passed {fmtFull(l.last_self_test_passed_at, tz)}</span> : <span className="chip">self-test not run</span>}
                  </div>
                </div>
                <div className="row">
                  {paused && (
                    <button className="btn small" onClick={() => resume.mutate(l.id)} disabled={resume.isPending}>
                      Resume
                    </button>
                  )}
                  <button className="btn small" onClick={() => setEditing(l)}>
                    Edit
                  </button>
                  <button className="btn small" onClick={() => update.mutate({ id: l.id, body: { active: !l.active } })} disabled={update.isPending}>
                    {l.active ? 'Turn off' : 'Turn on'}
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      {editing && <LineEditor line={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={refresh} />}
    </div>
  );
}

interface LineForm {
  phone_number: string;
  label: string;
  provider: ProviderId;
  keyMode: 'env' | 'value';
  api_key_env: string;
  api_key: string;
  signing_key: string;
  account_sid: string;
  messaging_profile_id: string;
  base_url: string;
  device_rate_per_minute: string;
  risk: boolean;
}

const DEFAULT_KEY_ENV: Record<ProviderId, string> = { httpsms: 'HTTPSMS_API_KEY', telnyx: 'TELNYX_API_KEY', textgrid: 'TEXTGRID_AUTH_TOKEN' };

function LineEditor({ line, onClose, onSaved }: { line: Line | null; onClose: () => void; onSaved: () => void }) {
  const pc = (line?.provider_config ?? {}) as Record<string, string | undefined>;
  const [f, setF] = useState<LineForm>({
    phone_number: line?.phone_number ?? '',
    label: line?.label ?? '',
    provider: line?.provider ?? 'httpsms',
    keyMode: pc.api_key ? 'value' : 'env',
    api_key_env: pc.api_key_env ?? pc.auth_token_env ?? DEFAULT_KEY_ENV[line?.provider ?? 'httpsms'],
    api_key: '',
    signing_key: '',
    account_sid: pc.account_sid ?? '',
    messaging_profile_id: pc.messaging_profile_id ?? '',
    base_url: pc.base_url ?? '',
    device_rate_per_minute: line?.device_rate_per_minute != null ? String(line.device_rate_per_minute) : '10',
    risk: false,
  });
  const set = <K extends keyof LineForm>(k: K, v: LineForm[K]) => setF((x) => ({ ...x, [k]: v }));
  // switching provider on an edit resets the env-name default
  useEffect(() => {
    if (!line || f.provider !== line.provider) set('api_key_env', DEFAULT_KEY_ENV[f.provider]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.provider]);

  const e164 = normalizeE164(f.phone_number);
  const needsRisk = f.provider === 'httpsms' && (!line || line.provider !== 'httpsms' || !line.risk_acknowledged_at);
  const switching = !!line && f.provider !== line.provider;

  const buildConfig = (): Record<string, unknown> => {
    const cfg: Record<string, unknown> = {};
    if (f.provider === 'textgrid') {
      cfg.account_sid = f.account_sid.trim();
      if (f.keyMode === 'env') cfg.auth_token_env = f.api_key_env.trim();
      else if (f.api_key) cfg.auth_token = f.api_key;
      if (f.signing_key) cfg.webhook_secret = f.signing_key;
    } else {
      if (f.keyMode === 'env') cfg.api_key_env = f.api_key_env.trim();
      else if (f.api_key) cfg.api_key = f.api_key;
      if (f.provider === 'httpsms' && f.signing_key) cfg.webhook_signing_key = f.signing_key;
      if (f.provider === 'telnyx') {
        if (f.signing_key) cfg.public_key = f.signing_key;
        if (f.messaging_profile_id) cfg.messaging_profile_id = f.messaging_profile_id.trim();
      }
    }
    if (f.base_url.trim()) cfg.base_url = f.base_url.trim();
    // keep keys the server already has when the person did not retype them
    if (line && !switching) {
      for (const k of ['api_key', 'auth_token', 'webhook_signing_key', 'webhook_signing_key_env', 'public_key', 'webhook_secret']) {
        if (cfg[k] === undefined && pc[k] !== undefined) cfg[k] = pc[k];
      }
    }
    return cfg;
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        phone_number: e164!,
        label: f.label.trim() || null,
        provider: f.provider,
        provider_config: buildConfig(),
        device_rate_per_minute: f.provider === 'httpsms' && f.device_rate_per_minute ? Number(f.device_rate_per_minute) : null,
        risk_acknowledged: f.provider === 'httpsms' ? f.risk || !!line?.risk_acknowledged_at : undefined,
      };
      return line ? api.lines.update(line.id, body) : api.lines.create({ ...body, label: body.label ?? undefined });
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  const keyLabel = f.provider === 'textgrid' ? 'Auth token' : 'API key';
  const sigLabel = f.provider === 'httpsms' ? 'Secret from the phone account (Webhooks section)' : f.provider === 'telnyx' ? 'Telnyx public key (Mission Control, Public key)' : 'TextGrid secret';
  const canSave = !!e164 && (!needsRisk || f.risk) && (f.provider !== 'textgrid' || !!f.account_sid.trim()) && (f.keyMode === 'env' ? !!f.api_key_env.trim() : !!f.api_key || !!line) && !save.isPending;

  return (
    <Dialog title={line ? 'Edit line' : 'Add line'} onClose={onClose} wide>
      {switching && <div className="notice info" style={{ marginBottom: 12 }}>Switching the provider keeps every conversation. Only the sending route changes; a note goes into each open thread.</div>}
      <label className="field">
        <span>Provider</span>
        <select className="input" value={f.provider} onChange={(e) => set('provider', e.target.value as ProviderId)}>
          <option value="httpsms">An Android phone (cheap, but the carrier can cut it off)</option>
          <option value="telnyx">Telnyx (registered number)</option>
          <option value="textgrid">TextGrid (registered number)</option>
        </select>
      </label>
      <div className="row" style={{ alignItems: 'flex-start' }}>
        <label className="field grow">
          <span>Phone number</span>
          <input className="input" value={f.phone_number} onChange={(e) => set('phone_number', e.target.value)} placeholder="(239) 555-0123" />
          {f.phone_number && !e164 && <div className="tiny danger-text">Not a full number yet.</div>}
        </label>
        <label className="field grow">
          <span>Label (optional)</span>
          <input className="input" value={f.label} onChange={(e) => set('label', e.target.value)} placeholder="Office phone" />
        </label>
      </div>
      {f.provider === 'textgrid' && (
        <label className="field">
          <span>Account SID</span>
          <input className="input" value={f.account_sid} onChange={(e) => set('account_sid', e.target.value)} />
        </label>
      )}
      {f.provider === 'telnyx' && (
        <label className="field">
          <span>Messaging profile ID (optional)</span>
          <input className="input" value={f.messaging_profile_id} onChange={(e) => set('messaging_profile_id', e.target.value)} />
        </label>
      )}
      <div className="field-label">{keyLabel}</div>
      <div className="row" style={{ marginBottom: 8 }}>
        <label className="row">
          <input type="radio" checked={f.keyMode === 'env'} onChange={() => set('keyMode', 'env')} /> Read it from the server's environment (recommended)
        </label>
        <label className="row">
          <input type="radio" checked={f.keyMode === 'value'} onChange={() => set('keyMode', 'value')} /> Paste it here
        </label>
      </div>
      {f.keyMode === 'env' ? (
        <label className="field">
          <span>Environment variable name</span>
          <input className="input mono" value={f.api_key_env} onChange={(e) => set('api_key_env', e.target.value)} />
          <div className="field-help">Put the key in the server's .env under this name. Nothing secret is stored in the database.</div>
        </label>
      ) : (
        <label className="field">
          <span>{keyLabel}</span>
          <input className="input mono" type="password" value={f.api_key} onChange={(e) => set('api_key', e.target.value)} placeholder={line ? 'leave blank to keep the current one' : ''} />
        </label>
      )}
      <label className="field">
        <span>{sigLabel}</span>
        <input className="input mono" type="password" value={f.signing_key} onChange={(e) => set('signing_key', e.target.value)} placeholder={line && (pc.webhook_signing_key || pc.public_key || pc.webhook_secret || pc.webhook_signing_key_env) ? 'set; leave blank to keep' : 'optional but recommended'} />
        <div className="field-help">Lets the app check that a reply really came from your phone, and not from someone pretending.</div>
      </label>
      {f.provider === 'httpsms' && (
        <label className="field">
          <span>Device rate (texts per minute the phone allows)</span>
          <input className="input" type="number" min={1} max={60} value={f.device_rate_per_minute} onChange={(e) => set('device_rate_per_minute', e.target.value)} />
          <div className="field-help">Set the same number in the texting app on the phone. Below 6 the texts go out in too even a rhythm, which carriers notice.</div>
        </label>
      )}
      <label className="field">
        <span>API base URL (leave blank unless testing against the fake phone)</span>
        <input className="input mono" value={f.base_url} onChange={(e) => set('base_url', e.target.value)} placeholder={f.provider === 'httpsms' ? 'https://api.httpsms.com/v1' : ''} />
      </label>
      {needsRisk && (
        <label className="check" style={{ background: 'var(--warn-soft)', padding: 10, borderRadius: 8 }}>
          <input type="checkbox" checked={f.risk} onChange={(e) => set('risk', e.target.checked)} />
          <span>{RISK_TEXT}</span>
        </label>
      )}
      <InlineError error={save.error} />
      <div className="actions">
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary" disabled={!canSave} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving' : line ? 'Save' : 'Add line'}
        </button>
      </div>
    </Dialog>
  );
}
